type DownloadPdfFromHtmlParams = {
  html: string;
  style: string;
  filename?: string;
};

const A4_WIDTH_PX = 794;
const LOGO_SRC = '/assets/pw_ioi/img/pwioi.png';
const MAIL_SRC = '/assets/pw_ioi/img/mail.png';

/** Renders backend HTML + CSS into an A4 PDF and downloads it. */
export const downloadPdfFromHtml = async ({
  html,
  style,
  filename = 'fee-structure.pdf',
}: DownloadPdfFromHtmlParams): Promise<void> => {
  const { default: html2pdf } = await import('html2pdf.js');

  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: `${A4_WIDTH_PX}px`,
    background: '#fff',
    pointerEvents: 'none',
    zIndex: '-1',
    clipPath: 'inset(50%)',
  });

  const styleEl = document.createElement('style');
  // Strip screen-only print-preview chrome that creates empty page gaps.
  const cleanStyle = style
    .replace(/@media\s+screen\s*\{[\s\S]*?\n\}/gi, '')
    .replace(/min-height\s*:\s*11\.69in\s*;?/gi, '');

  styleEl.textContent = `
    ${cleanStyle}
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .print-format { min-height: 0 !important; background: #fff; color: #192734; font-size: 13px; }
    .print-format .letterhead {
      display: flex !important;
      justify-content: space-between !important;
      border-bottom: 2px solid #555 !important;
      padding-bottom: 16px !important;
      margin-bottom: 20px !important;
    }
    .print-format .logo-section img { height: 48px !important; width: auto !important; max-width: 240px !important; }
    .print-format table { width: 100% !important; border-collapse: collapse !important; margin: 16px 0 !important; }
    .print-format th, .print-format td {
      border: 1px solid #c9cdd1 !important;
      padding: 8px 6px !important;
      color: #192734 !important;
    }
    .print-format th { background: #f7f8f9 !important; }
    .print-format tr[style*="background"] { background-color: #f0f0f0 !important; }
    .print-format .footer-content {
      display: flex !important;
      justify-content: space-around !important;
      align-items: center !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
    }
    .print-format .footer-content img { height: 14px !important; width: 14px !important; }
    .print-format .page-break {
      page-break-after: always !important;
      height: 0 !important;
      border: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  `;

  const content = document.createElement('div');
  content.className = 'print-format';
  content.innerHTML = html;

  container.append(styleEl, content);
  document.body.appendChild(container);

  try {
    content.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src') || '';
      if (img.closest('.logo-section') || /pwioi\.png|logo/i.test(src + img.alt)) {
        img.src = LOGO_SRC;
      } else if (img.closest('.footer') || /mail|email|wikimedia|\.svg$/i.test(src + img.alt)) {
        img.src = MAIL_SRC;
      }
    });

    await Promise.all(
      Array.from(content.querySelectorAll('img')).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) resolve();
            else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          })
      )
    );

    await html2pdf()
      .set({
        margin: 0.4,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: A4_WIDTH_PX,
        },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], after: ['.page-break'] },
      } as never)
      .from(content)
      .save();
  } finally {
    container.remove();
  }
};
